import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components';
import type {
  DerivedCase,
  DerivedValue,
  FactType,
  ItemFieldType,
  ListItemField,
  ScenarioConfig,
  ScenarioFact,
  ScenarioIssue,
} from './types';
import {
  DERIVED_TYPES,
  ITEM_FIELD_TYPES,
  SCALAR_TYPES,
  WEEKDAYS,
  isDerivedFact,
  moveItem,
  newDerivedCase,
  newFact,
  newItemField,
  omitKeys,
  removeAt,
  renameFactAt,
  setIn,
  withDerivedDefault,
  withFactSourceAt,
  withFactTypeAt,
  withItemFieldRequired,
  withItemFieldType,
  withItemFieldWeekdays,
} from './scenarioDraft';
import { ConditionEditor } from './ConditionEditor';
import {
  AddButton,
  IssueList,
  ItemRow,
  MultiSelectField,
  NameField,
  RowActions,
  Section,
  SelectField,
  TextField,
  Toggle,
} from './ui';

const FACT_TYPES: FactType[] = [...SCALAR_TYPES, 'list'];

interface Props {
  config: ScenarioConfig;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
}

const parseNumber = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const withOptionalNumber = <T extends object>(target: T, key: string, value: string): T => {
  const parsed = parseNumber(value);
  const copy = { ...target } as Record<string, unknown>;
  if (parsed === undefined) delete copy[key];
  else copy[key] = parsed;
  return copy as T;
};

const ItemFieldsEditor: React.FC<{
  config: ScenarioConfig;
  fact: ScenarioFact;
  factIndex: number;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
}> = ({ config, fact, factIndex, onChange, issues }) => {
  const { t } = useTranslation();
  const fields = fact.item_fields ?? [];
  const basePath = ['facts', factIndex, 'item_fields'];
  const ptr = `/facts/${factIndex}/item_fields`;
  const listFacts = config.facts.filter(f => f.type === 'list' && f.name !== fact.name);

  const update = (index: number, field: ListItemField) => onChange(setIn(config, [...basePath, index], field));

  return (
    <div className="space-y-2">
      <IssueList issues={issues} pointer={ptr} />
      {fields.map((field, index) => {
        const fptr = `${ptr}/${index}`;
        const isNumeric = field.type === 'integer' || field.type === 'number';
        const source = config.facts.find(f => f.name === field.allowed_values_from?.fact);
        return (
          <ItemRow key={index} className="grid grid-cols-1 gap-2 md:grid-cols-6">
            <TextField
              label={t('agents.scenario.fieldName')}
              value={field.name}
              onChange={value => update(index, { ...field, name: value })}
              issues={issues}
              pointer={`${fptr}/name`}
              monospace
            />
            <SelectField
              label={t('agents.scenario.type')}
              value={field.type}
              options={ITEM_FIELD_TYPES.map(type => ({ value: type, label: type }))}
              onChange={value => update(index, withItemFieldType(field, value as ItemFieldType))}
              issues={issues}
              pointer={`${fptr}/type`}
            />
            {isNumeric ? (
              <>
                <TextField
                  label={t('agents.scenario.min')}
                  type="number"
                  value={field.min?.toString() ?? ''}
                  onChange={value => update(index, withOptionalNumber(field, 'min', value))}
                  issues={issues}
                  pointer={`${fptr}/min`}
                />
                <TextField
                  label={t('agents.scenario.max')}
                  type="number"
                  value={field.max?.toString() ?? ''}
                  onChange={value => update(index, withOptionalNumber(field, 'max', value))}
                  issues={issues}
                  pointer={`${fptr}/max`}
                />
              </>
            ) : field.type === 'string_list' ? (
              <>
                <TextField
                  label={t('agents.scenario.maxItems')}
                  type="number"
                  value={field.max_items?.toString() ?? ''}
                  onChange={value => update(index, withOptionalNumber(field, 'max_items', value))}
                  issues={issues}
                  pointer={`${fptr}/max_items`}
                />
                <div />
              </>
            ) : field.type === 'date' ? (
              <div className="md:col-span-2">
                <MultiSelectField
                  label={t('agents.scenario.weekdays')}
                  values={field.weekdays ?? []}
                  options={WEEKDAYS.map(day => ({
                    value: day,
                    label: t(`agents.scenario.weekdayNames.${day}`),
                  }))}
                  onChange={values => update(index, withItemFieldWeekdays(field, values))}
                  issues={issues}
                  pointer={`${fptr}/weekdays`}
                />
              </div>
            ) : (
              <div className="md:col-span-2" />
            )}
            <SelectField
              label={t('agents.scenario.allowedValuesFrom')}
              value={field.allowed_values_from?.fact ?? ''}
              options={[
                { value: '', label: t('agents.scenario.anyValue') },
                ...listFacts.map(f => ({ value: f.name, label: f.name })),
              ]}
              onChange={value => {
                const rest = omitKeys(field, 'allowed_values_from');
                update(index, value ? { ...rest, allowed_values_from: { fact: value, property: '' } } : rest);
              }}
              issues={issues}
              pointer={`${fptr}/allowed_values_from/fact`}
            />
            <div className="flex items-end gap-1">
              {field.allowed_values_from && (
                <div className="flex-1">
                  <SelectField
                    label={t('agents.scenario.property')}
                    value={field.allowed_values_from.property}
                    options={(source?.item_fields ?? []).map(f => ({ value: f.name, label: f.name }))}
                    onChange={value =>
                      update(index, {
                        ...field,
                        allowed_values_from: { fact: field.allowed_values_from?.fact ?? '', property: value },
                      })
                    }
                    issues={issues}
                    pointer={`${fptr}/allowed_values_from/property`}
                  />
                </div>
              )}
              <RowActions
                index={index}
                count={fields.length}
                onMove={(from, to) => onChange(setIn(config, basePath, moveItem(fields, from, to)))}
                onRemove={() => onChange(setIn(config, basePath, removeAt(fields, index)))}
              />
            </div>
            <div className="md:col-span-6">
              <Toggle
                label={t('agents.scenario.required')}
                checked={field.required !== false}
                onChange={value => update(index, withItemFieldRequired(field, value))}
              />
              <IssueList issues={issues} pointer={`${fptr}/required`} />
              <IssueList issues={issues} pointer={fptr} />
            </div>
          </ItemRow>
        );
      })}
      <AddButton
        label={t('agents.scenario.addItemField')}
        onClick={() => onChange(setIn(config, basePath, [...fields, newItemField(fields)]))}
      />
    </div>
  );
};

interface DerivedValueFieldProps {
  type: FactType;
  label: string;
  value: DerivedValue | undefined;
  onChange: (value: DerivedValue | undefined) => void;
  issues: ScenarioIssue[];
  pointer: string;
  hint?: string;
  /** An optional value (the default) can be left empty. */
  optional?: boolean;
}

const DerivedValueField: React.FC<DerivedValueFieldProps> = ({
  type,
  label,
  value,
  onChange,
  issues,
  pointer,
  hint,
  optional = false,
}) => {
  const { t } = useTranslation();
  if (type === 'boolean') {
    return (
      <SelectField
        label={label}
        hint={hint}
        value={value === undefined ? '' : String(value)}
        options={[
          ...(optional ? [{ value: '', label: t('agents.scenario.derived.noValue') }] : []),
          { value: 'true', label: t('agents.scenario.derived.true') },
          { value: 'false', label: t('agents.scenario.derived.false') },
        ]}
        onChange={next => onChange(next === '' ? undefined : next === 'true')}
        issues={issues}
        pointer={pointer}
      />
    );
  }
  return (
    <TextField
      label={label}
      hint={hint}
      value={value === undefined ? '' : String(value)}
      placeholder={optional ? t('agents.scenario.derived.noValue') : undefined}
      onChange={onChange}
      issues={issues}
      pointer={pointer}
      monospace
    />
  );
};

const DerivedCasesEditor: React.FC<{
  config: ScenarioConfig;
  fact: ScenarioFact;
  factIndex: number;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
}> = ({ config, fact, factIndex, onChange, issues }) => {
  const { t } = useTranslation();
  const cases = fact.cases ?? [];
  const basePath = ['facts', factIndex, 'cases'];
  const ptr = `/facts/${factIndex}/cases`;

  const update = (index: number, item: DerivedCase) => onChange(setIn(config, [...basePath, index], item));

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.derived.hint')}</p>
      <IssueList issues={issues} pointer={ptr} />
      {cases.map((item, index) => {
        const cptr = `${ptr}/${index}`;
        return (
          <ItemRow key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('agents.scenario.derived.case', { number: index + 1 })}
              </span>
              <RowActions
                index={index}
                count={cases.length}
                onMove={(from, to) => onChange(setIn(config, basePath, moveItem(cases, from, to)))}
                onRemove={() => onChange(setIn(config, basePath, removeAt(cases, index)))}
              />
            </div>
            <ConditionEditor
              config={config}
              value={item.when}
              onChange={when => update(index, { ...item, when })}
              issues={issues}
              pointer={`${cptr}/when`}
              allowDerived={false}
            />
            <div className="max-w-xs">
              <DerivedValueField
                type={fact.type}
                label={t('agents.scenario.derived.value')}
                value={item.value}
                onChange={value => update(index, { ...item, value: value ?? '' })}
                issues={issues}
                pointer={`${cptr}/value`}
              />
            </div>
            <IssueList issues={issues} pointer={cptr} />
          </ItemRow>
        );
      })}
      <AddButton
        label={t('agents.scenario.derived.addCase')}
        onClick={() => onChange(setIn(config, basePath, [...cases, newDerivedCase(fact.type)]))}
      />
      <div className="max-w-xs">
        <DerivedValueField
          type={fact.type}
          label={t('agents.scenario.derived.default')}
          hint={t('agents.scenario.derived.defaultHint')}
          value={fact.default}
          onChange={value => onChange(setIn(config, ['facts', factIndex], withDerivedDefault(fact, value)))}
          issues={issues}
          pointer={`/facts/${factIndex}/default`}
          optional
        />
      </div>
    </div>
  );
};

const SOURCE_BADGES = {
  api: { variant: 'info', label: 'agents.scenario.sourceApi' },
  caller: { variant: 'default', label: 'agents.scenario.sourceCaller' },
  derived: { variant: 'accent', label: 'agents.scenario.sourceDerived' },
} as const;

export const FactsEditor: React.FC<Props> = ({ config, onChange, issues }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {config.facts.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('agents.scenario.noFacts')}</p>
      )}
      {config.facts.map((fact, index) => {
        const ptr = `/facts/${index}`;
        const derived = isDerivedFact(fact);
        const sourceBadge = SOURCE_BADGES[fact.source] ?? SOURCE_BADGES.caller;
        return (
          <Section
            key={index}
            title={fact.name || '—'}
            badges={
              <>
                <Badge variant="outline" size="sm">
                  {t(`agents.scenario.types.${fact.type}`)}
                </Badge>
                <Badge variant={sourceBadge.variant} size="sm">
                  {t(sourceBadge.label)}
                </Badge>
                <Badge variant="secondary" size="sm">
                  {t(fact.visibility === 'ai' ? 'agents.scenario.visibilityAi' : 'agents.scenario.visibilityInternal')}
                </Badge>
              </>
            }
            issues={issues}
            pointer={ptr}
            defaultOpen={false}
            actions={
              <RowActions
                index={index}
                count={config.facts.length}
                onMove={(from, to) => onChange({ ...config, facts: moveItem(config.facts, from, to) })}
                onRemove={() => onChange({ ...config, facts: removeAt(config.facts, index) })}
              />
            }
          >
            <IssueList issues={issues} pointer={ptr} />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <NameField
                label={t('agents.scenario.technicalName')}
                value={fact.name}
                onCommit={value => onChange(renameFactAt(config, index, value))}
                issues={issues}
                pointer={`${ptr}/name`}
              />
              <SelectField
                label={t('agents.scenario.type')}
                value={fact.type}
                options={(derived ? DERIVED_TYPES : FACT_TYPES).map(type => ({
                  value: type,
                  label: t(`agents.scenario.types.${type}`),
                }))}
                onChange={value => onChange(withFactTypeAt(config, index, value as FactType))}
                issues={issues}
                pointer={`${ptr}/type`}
              />
              <SelectField
                label={t('agents.scenario.source')}
                value={fact.source}
                options={[
                  { value: 'api', label: t('agents.scenario.sourceApi') },
                  { value: 'caller', label: t('agents.scenario.sourceCaller') },
                  { value: 'derived', label: t('agents.scenario.sourceDerived') },
                ]}
                onChange={value => onChange(withFactSourceAt(config, index, value as ScenarioFact['source']))}
                issues={issues}
                pointer={`${ptr}/source`}
              />
              <SelectField
                label={t('agents.scenario.visibility')}
                value={fact.visibility}
                options={[
                  { value: 'ai', label: t('agents.scenario.visibilityAi') },
                  { value: 'internal', label: t('agents.scenario.visibilityInternal') },
                ]}
                onChange={value => onChange(setIn(config, ['facts', index, 'visibility'], value))}
                issues={issues}
                pointer={`${ptr}/visibility`}
              />
            </div>
            {fact.type === 'list' && (
              <>
                <div className="max-w-xs">
                  <TextField
                    label={t('agents.scenario.maxItems')}
                    type="number"
                    value={fact.max_items?.toString() ?? ''}
                    onChange={value =>
                      onChange(setIn(config, ['facts', index], withOptionalNumber(fact, 'max_items', value)))
                    }
                    issues={issues}
                    pointer={`${ptr}/max_items`}
                  />
                </div>
                <ItemFieldsEditor config={config} fact={fact} factIndex={index} onChange={onChange} issues={issues} />
              </>
            )}
            {derived && (
              <DerivedCasesEditor config={config} fact={fact} factIndex={index} onChange={onChange} issues={issues} />
            )}
          </Section>
        );
      })}
      <AddButton
        label={t('agents.scenario.addFact')}
        onClick={() => onChange({ ...config, facts: [...config.facts, newFact(config)] })}
      />
    </div>
  );
};
